import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { printReport } from '../utils/printReport';
import MonthlyReport from './MonthlyReport';
import { PRIORITY_AR } from '../utils/labels';

const API = process.env.REACT_APP_API || '/api';


export default function Reports() {
  const { api, token } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState('team');
  const [teamData, setTeamData] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [overdueData, setOverdueData] = useState([]);
  const [bottlenecks, setBottlenecks] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [sheikhs, setSheikhs] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [months, setMonths] = useState([]);
  const [range, setRange] = useState({ month: '', from: '', to: '' });
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(true);

  const rangeQs = useMemo(() => {
    const qs = new URLSearchParams();
    if (range.month) qs.set('month', range.month);
    else {
      if (range.from) qs.set('from', range.from);
      if (range.to) qs.set('to', range.to);
    }
    const s = qs.toString();
    return s ? `?${s}` : '';
  }, [range]);

  useEffect(() => {
    api('/reports/months').then(m => {
      setMonths(m);
      if (m.length) setSelectedMonth(m[0].value);
    }).catch(() => {});
    Promise.all([
      api('/reports/overdue').then(setOverdueData).catch(() => {}),
      api('/reports/workflow-bottlenecks').then(setBottlenecks).catch(() => {}),
      api('/reports/at-risk').then(setAtRisk).catch(() => {}),
      api('/clients').then(d => setSheikhs(d.filter(c => c.type === 'sheikh'))).catch(() => {}),
      api('/sponsors?active=1').then(setSponsors).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api(`/reports/team-performance${rangeQs}`).then(setTeamData).catch(() => {});
    api(`/reports/client-status${rangeQs}`).then(setClientData).catch(() => {});
  }, [rangeQs]);

  const tabs = [
    { key: 'team', label: 'أداء الفريق' },
    { key: 'clients', label: 'حالة العملاء' },
    { key: 'overdue', label: 'المتأخرات' },
    { key: 'atrisk', label: `معرّضة للخطر${atRisk.length ? ` (${atRisk.length})` : ''}` },
    { key: 'bottlenecks', label: 'نقاط الاختناق' },
    { key: 'monthly', label: '📅 التقرير الشهري' },
    { key: 'downloads', label: '📥 تنزيل تقارير الأشهر' },
    { key: 'sheikhs', label: 'تقارير المشايخ' },
    { key: 'sponsors', label: 'تقارير الداعمين' },
  ];

  const rangeAware = tab === 'team' || tab === 'clients';

  const teamTotals = useMemo(() => {
    const sum = (k) => teamData.reduce((s, r) => s + (r[k] || 0), 0);
    const avgOf = (k) => {
      const vals = teamData.map(r => r[k]).filter(v => v != null);
      return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
    };
    return {
      members: teamData.length,
      completed: sum('completed'),
      active: sum('active'),
      overdue: sum('overdue'),
      avg: avgOf('avg_days'),
      cycle: avgOf('cycle_days'),
      onTime: avgOf('on_time_rate'),
    };
  }, [teamData]);

  const clientTotals = useMemo(() => {
    const sum = (k) => clientData.reduce((s, r) => s + (r[k] || 0), 0);
    const rates = clientData.map(r => r.on_time_rate).filter(v => v != null);
    return {
      clients: clientData.length,
      projects: sum('projects'),
      active: sum('active_tasks'),
      awaiting: sum('awaiting_approval'),
      completed: sum('completed_tasks'),
      onTime: rates.length ? Math.round(rates.reduce((a, b) => a + b, 0) / rates.length) : null,
    };
  }, [clientData]);

  const overdueBuckets = useMemo(() => {
    let low = 0, mid = 0, high = 0;
    overdueData.forEach(r => {
      const d = r.days_overdue || 0;
      if (d <= 3) low++; else if (d <= 7) mid++; else high++;
    });
    return { low, mid, high };
  }, [overdueData]);

  const overdueByAssignee = useMemo(() => {
    const map = {};
    overdueData.forEach(r => {
      const name = r.assignee_name || '— غير مسند';
      map[name] = map[name] || { name, count: 0, days: 0 };
      map[name].count++;
      map[name].days += r.days_overdue || 0;
    });
    return Object.values(map).sort((a, b) => b.days - a.days);
  }, [overdueData]);

  const completionPct = (r) => {
    const denom = (r.completed_tasks || 0) + (r.active_tasks || 0);
    if (!denom) return null;
    return Math.round((r.completed_tasks || 0) / denom * 100);
  };

  const exportCsv = async (type) => {
    try {
      const qs = rangeAware ? rangeQs : '';
      const res = await fetch(`${API}/reports/export/${type}${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('فشل التصدير');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${type}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const currentExportType = {
    team: 'team-performance', clients: 'client-status',
    overdue: 'overdue', bottlenecks: 'workflow-bottlenecks', atrisk: 'at-risk'
  }[tab];

  const rangeLabel = () => {
    if (range.month) {
      const m = months.find(mm => mm.value === range.month);
      return m ? m.label : range.month;
    }
    if (range.from || range.to) return `من ${range.from || '—'} إلى ${range.to || '—'}`;
    return 'كامل المدة';
  };

  const printCurrent = () => {
    const configs = {
      team: {
        title: 'تقرير أداء الفريق',
        subtitle: rangeLabel(),
        columns: [
          { key: 'name', label: 'العضو' },
          { key: 'completed', label: 'مكتملة', badge: v => 'success' },
          { key: 'active', label: 'نشطة', badge: v => 'info' },
          { key: 'overdue', label: 'متأخرة', badge: v => v > 0 ? 'danger' : 'success' },
          { key: 'on_time_rate', label: 'الالتزام بالموعد %' },
          { key: 'cycle_days', label: 'زمن التنفيذ (أيام)' },
          { key: 'avg_days', label: 'زمن الإنجاز (أيام)' },
        ],
        rows: teamData,
      },
      clients: {
        title: 'تقرير حالة العملاء',
        subtitle: rangeLabel(),
        columns: [
          { key: 'name', label: 'العميل' },
          { key: 'type', label: 'النوع', transform: v => v === 'sheikh' ? 'شيخ' : v === 'association' ? 'جمعية' : 'أخرى' },
          { key: 'projects', label: 'المشاريع' },
          { key: 'active_tasks', label: 'مهام نشطة' },
          { key: 'awaiting_approval', label: 'بانتظار اعتماد', badge: v => v > 0 ? 'warn' : 'success' },
          { key: 'completed_tasks', label: 'مكتملة' },
          { key: 'on_time_rate', label: 'الالتزام بالموعد %' },
        ],
        rows: clientData,
      },
      overdue: {
        title: 'تقرير المهام المتأخرة',
        subtitle: `إجمالي: ${overdueData.length} مهمة متأخرة`,
        columns: [
          { key: 'title', label: 'المهمة' },
          { key: 'assignee_name', label: 'المسؤول' },
          { key: 'client_name', label: 'العميل' },
          { key: 'priority', label: 'الأولوية', transform: v => PRIORITY_AR[v] || v },
          { key: 'days_overdue', label: 'أيام التأخر', badge: () => 'danger' },
        ],
        rows: overdueData,
      },
      atrisk: {
        title: 'تقرير المهام المعرّضة للخطر',
        subtitle: `إجمالي: ${atRisk.length} مهمة معرّضة للخطر`,
        columns: [
          { key: 'title', label: 'المهمة' },
          { key: 'assignee_name', label: 'المسؤول' },
          { key: 'client_name', label: 'العميل' },
          { key: 'priority', label: 'الأولوية', transform: v => PRIORITY_AR[v] || v },
          { key: 'due_date', label: 'الاستحقاق' },
          { key: 'reasons', label: 'أسباب الخطر', transform: v => Array.isArray(v) ? v.join(' • ') : v },
        ],
        rows: atRisk,
      },
      bottlenecks: {
        title: 'تقرير نقاط الاختناق في المسارات',
        columns: [
          { key: 'workflow_name', label: 'المسار' },
          { key: 'step_name', label: 'الخطوة' },
          { key: 'stuck_count', label: 'عدد المهام العالقة', badge: () => 'warn' },
          { key: 'avg_days_stuck', label: 'متوسط أيام التوقف', transform: v => Math.round(v) },
          { key: 'max_days_stuck', label: 'أقصى توقف (أيام)', transform: v => Math.round(v) },
        ],
        rows: bottlenecks,
      },
    };
    const cfg = configs[tab];
    if (!cfg) return;
    printReport(cfg);
  };

  if (loading) return <div className="loader">جاري التحميل...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>التقارير</h2>
        {currentExportType && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={printCurrent}>
              🖨 طباعة / PDF
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => exportCsv(currentExportType)}>
              📥 تصدير CSV
            </button>
          </div>
        )}
      </div>
      <div className="filters" style={{ marginBottom: 16 }}>
        {tabs.map(t => (
          <button key={t.key} className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {rangeAware && (
        <div className="card" style={{ padding: 12, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, alignItems: 'end' }}>
            <div>
              <label className="form-label">شهر مغلق</label>
              <select className="form-select" value={range.month}
                onChange={e => setRange({ month: e.target.value, from: '', to: '' })}>
                <option value="">— مدى مخصّص —</option>
                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">من تاريخ</label>
              <input className="form-input" type="date" disabled={!!range.month}
                value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">إلى تاريخ</label>
              <input className="form-input" type="date" disabled={!!range.month}
                value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} />
            </div>
            {(range.month || range.from || range.to) && (
              <div>
                <button className="btn btn-outline btn-sm" onClick={() => setRange({ month: '', from: '', to: '' })}>↺ كامل المدة</button>
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            تُطبَّق الفترة على المهام المكتملة خلالها · {rangeLabel()}
          </div>
        </div>
      )}

      {tab === 'team' && (
        <>
          <SummaryCards items={[
            { label: 'الأعضاء', value: teamTotals.members },
            { label: 'إجمالي المكتمل', value: teamTotals.completed, color: 'var(--success)' },
            { label: 'إجمالي المتأخر', value: teamTotals.overdue, color: 'var(--danger)' },
            { label: 'الالتزام بالموعد', value: teamTotals.onTime == null ? '—' : `${teamTotals.onTime}%`, color: rateColor(teamTotals.onTime) },
            { label: 'زمن التنفيذ (أيام)', value: teamTotals.cycle ?? '—' },
          ]} />
          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>أداء أعضاء الفريق</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>العضو</th><th>مكتملة</th><th>نشطة</th><th>متأخرة</th><th>الالتزام بالموعد</th><th>زمن التنفيذ (أيام)</th><th>زمن الإنجاز (أيام)</th></tr></thead>
                <tbody>
                  {teamData.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 500 }}>{r.name}</td>
                      <td><span className="badge badge-completed">{r.completed}</span></td>
                      <td><span className="badge badge-in_progress">{r.active}</span></td>
                      <td>{r.overdue > 0 ? <span className="badge badge-revision">{r.overdue}</span> : <span className="badge badge-completed">0</span>}</td>
                      <td>{r.on_time_rate == null ? '—' : <span style={{ fontWeight: 600, color: rateColor(r.on_time_rate) }}>{r.on_time_rate}%</span>}</td>
                      <td>{r.cycle_days != null ? Math.round(r.cycle_days) : '—'}</td>
                      <td>{r.avg_days != null ? Math.round(r.avg_days) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                {teamData.length > 0 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                      <td>الإجمالي / المتوسط</td>
                      <td>{teamTotals.completed}</td>
                      <td>{teamTotals.active}</td>
                      <td>{teamTotals.overdue}</td>
                      <td>{teamTotals.onTime == null ? '—' : `${teamTotals.onTime}%`}</td>
                      <td>{teamTotals.cycle ?? '—'}</td>
                      <td>{teamTotals.avg ?? '—'}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'clients' && (
        <>
          <SummaryCards items={[
            { label: 'العملاء', value: clientTotals.clients },
            { label: 'مهام نشطة', value: clientTotals.active, color: 'var(--primary)' },
            { label: 'بانتظار اعتماد', value: clientTotals.awaiting, color: 'var(--warning)' },
            { label: 'مكتملة', value: clientTotals.completed, color: 'var(--success)' },
            { label: 'الالتزام بالموعد', value: clientTotals.onTime == null ? '—' : `${clientTotals.onTime}%`, color: rateColor(clientTotals.onTime) },
          ]} />
          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>حالة العملاء</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>العميل</th><th>النوع</th><th>المشاريع</th><th>مهام نشطة</th><th>بانتظار اعتماد</th><th>مكتملة</th><th>الالتزام بالموعد</th><th>نسبة الإنجاز</th></tr></thead>
                <tbody>
                  {clientData.map(r => {
                    const pct = completionPct(r);
                    return (
                      <tr key={r.id}>
                        <td style={{ fontWeight: 500 }}>{r.name}</td>
                        <td>{r.type === 'sheikh' ? 'شيخ' : r.type === 'association' ? 'جمعية' : 'أخرى'}</td>
                        <td>{r.projects}</td>
                        <td>{r.active_tasks}</td>
                        <td>{r.awaiting_approval > 0 ? <span className="badge badge-waiting_approval">{r.awaiting_approval}</span> : '0'}</td>
                        <td>{r.completed_tasks}</td>
                        <td>{r.on_time_rate == null ? '—' : <span style={{ fontWeight: 600, color: rateColor(r.on_time_rate) }}>{r.on_time_rate}%</span>}</td>
                        <td>{pct == null ? '—' : <ProgressPct pct={pct} />}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {clientData.length > 0 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                      <td>الإجمالي</td>
                      <td>—</td>
                      <td>{clientTotals.projects}</td>
                      <td>{clientTotals.active}</td>
                      <td>{clientTotals.awaiting}</td>
                      <td>{clientTotals.completed}</td>
                      <td>{clientTotals.onTime == null ? '—' : `${clientTotals.onTime}%`}</td>
                      <td>—</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'overdue' && (
        <>
          <SummaryCards items={[
            { label: 'إجمالي المتأخرات', value: overdueData.length, color: overdueData.length ? 'var(--danger)' : 'var(--success)' },
            { label: '≤ 3 أيام', value: overdueBuckets.low, color: 'var(--warning)' },
            { label: '4–7 أيام', value: overdueBuckets.mid, color: 'var(--warning)' },
            { label: 'أكثر من أسبوع', value: overdueBuckets.high, color: 'var(--danger)' },
          ]} />
          {overdueByAssignee.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-title" style={{ marginBottom: 12 }}>المتأخرات حسب المسؤول</div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>المسؤول</th><th>عدد المتأخرات</th><th>إجمالي أيام التأخر</th></tr></thead>
                  <tbody>
                    {overdueByAssignee.map(a => (
                      <tr key={a.name}>
                        <td style={{ fontWeight: 500 }}>{a.name}</td>
                        <td><span className="badge badge-revision">{a.count}</span></td>
                        <td>{a.days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>المهام المتأخرة ({overdueData.length})</div>
            {overdueData.length === 0 ? <div className="empty">لا توجد مهام متأخرة 🎉</div> : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>المهمة</th><th>المسؤول</th><th>العميل</th><th>الأولوية</th><th>أيام التأخر</th></tr></thead>
                  <tbody>
                    {overdueData.map(r => (
                      <tr key={r.id}>
                        <td style={{ fontWeight: 500 }}>{r.title}</td>
                        <td>{r.assignee_name || '—'}</td>
                        <td>{r.client_name || '—'}</td>
                        <td>{r.priority ? <span className={`badge badge-${r.priority}`}>{PRIORITY_AR[r.priority] || r.priority}</span> : '—'}</td>
                        <td><span className="badge badge-revision">{r.days_overdue} يوم</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'atrisk' && (
        <>
          <SummaryCards items={[
            { label: 'إجمالي المعرّضة للخطر', value: atRisk.length, color: atRisk.length ? 'var(--warning)' : 'var(--success)' },
            { label: 'لم تبدأ ويقترب استحقاقها', value: atRisk.filter(r => r.reasons.includes('لم تبدأ ويقترب استحقاقها')).length, color: 'var(--danger)' },
            { label: 'تجاوزت الوقت المقدّر', value: atRisk.filter(r => r.reasons.includes('تجاوزت الوقت المقدّر')).length, color: 'var(--warning)' },
            { label: 'بلا حركة منذ أسبوع', value: atRisk.filter(r => r.reasons.includes('بلا حركة منذ أكثر من أسبوع')).length, color: 'var(--warning)' },
          ]} />
          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>مهام معرّضة للخطر (إنذار مبكر)</div>
            {atRisk.length === 0 ? <div className="empty">لا توجد مهام معرّضة للخطر 🎉</div> : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>المهمة</th><th>المسؤول</th><th>العميل</th><th>الأولوية</th><th>الاستحقاق</th><th>أسباب الخطر</th></tr></thead>
                  <tbody>
                    {atRisk.map(r => (
                      <tr key={r.id}>
                        <td style={{ fontWeight: 500 }}>{r.title}</td>
                        <td>{r.assignee_name || '—'}</td>
                        <td>{r.client_name || '—'}</td>
                        <td>{r.priority ? <span className={`badge badge-${r.priority}`}>{PRIORITY_AR[r.priority] || r.priority}</span> : '—'}</td>
                        <td>{r.due_date || '—'}</td>
                        <td>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {r.reasons.map((reason, i) => (
                              <span key={i} className="badge badge-warning" style={{ fontSize: 11 }}>{reason}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'monthly' && (
        <>
          <div className="card" style={{ padding: 12, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
            <div>
              <label className="form-label">الشهر</label>
              <select className="form-select" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            {selectedMonth && (
              <a className="btn btn-primary btn-sm" href={`/reports/print/${selectedMonth}`} target="_blank" rel="noreferrer">
                🖨 تنزيل / طباعة هذا الشهر
              </a>
            )}
          </div>
          {selectedMonth && <MonthlyReport key={selectedMonth} month={selectedMonth} />}
        </>
      )}

      {tab === 'downloads' && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>تنزيل تقارير الأشهر المتتالية</div>
          {months.length === 0 ? <div className="empty">لا توجد أشهر متاحة</div> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {months.map(m => (
                <a key={m.value} href={`/reports/print/${m.value}`} target="_blank" rel="noreferrer"
                  className="card" style={{ padding: 16, textDecoration: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600 }}>{m.label}</span>
                  <span style={{ color: 'var(--primary)' }}>📥 تنزيل ←</span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'sheikhs' && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>اختر شيخاً لعرض تقريره التفصيلي</div>
          {sheikhs.length === 0 ? <div className="empty">لا يوجد مشايخ مسجّلون</div> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {sheikhs.map(s => (
                <Link key={s.id} to={`/reports/sheikh/${s.id}`}
                  className="card" style={{ padding: 14, textDecoration: 'none' }}>
                  <div style={{ fontWeight: 600 }}>{s.name}</div>
                  {s.contact_email && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{s.contact_email}</div>}
                  <div style={{ fontSize: 12, marginTop: 8, color: 'var(--primary)' }}>📊 عرض التقرير ←</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'sponsors' && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>اختر داعماً لعرض تقريره التفصيلي</div>
          {sponsors.length === 0 ? <div className="empty">لا يوجد داعمون</div> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {sponsors.map(s => (
                <Link key={s.id} to={`/reports/sponsor/${s.id}`}
                  className="card" style={{ padding: 14, textDecoration: 'none' }}>
                  <div style={{ fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    {s.type === 'institution' ? 'مؤسسة' : 'شخص'} • {s.total_projects || 0} مشروع
                  </div>
                  <div style={{ fontSize: 12, marginTop: 8, color: 'var(--primary)' }}>📊 عرض التقرير ←</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'bottlenecks' && (
        <>
          <SummaryCards items={[
            { label: 'خطوات مختنقة', value: bottlenecks.length, color: bottlenecks.length ? 'var(--warning)' : 'var(--success)' },
            { label: 'إجمالي المهام العالقة', value: bottlenecks.reduce((s, r) => s + (r.stuck_count || 0), 0), color: 'var(--danger)' },
            { label: 'أقصى توقف (أيام)', value: bottlenecks.length ? Math.round(Math.max(...bottlenecks.map(r => r.max_days_stuck || 0))) : 0 },
          ]} />
          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>نقاط الاختناق في المسارات</div>
            {bottlenecks.length === 0 ? <div className="empty">لا توجد اختناقات حالياً</div> : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>المسار</th><th>الخطوة</th><th>عدد المهام العالقة</th><th>متوسط أيام التوقف</th><th>أقصى توقف</th></tr></thead>
                  <tbody>
                    {bottlenecks.map((r, i) => (
                      <tr key={i}>
                        <td>{r.workflow_name}</td>
                        <td style={{ fontWeight: 500 }}>{r.step_name}</td>
                        <td><span className="badge badge-warning">{r.stuck_count}</span></td>
                        <td><span className="badge badge-revision">{Math.round(r.avg_days_stuck)} يوم</span></td>
                        <td><span className="badge badge-revision">{Math.round(r.max_days_stuck)} يوم</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function rateColor(pct) {
  if (pct == null) return 'var(--text)';
  return pct >= 80 ? 'var(--success)' : pct >= 50 ? 'var(--warning)' : 'var(--danger)';
}

function SummaryCards({ items }) {
  return (
    <div className="card" style={{ padding: 14, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
        {items.map((it, i) => (
          <div key={i} style={{ textAlign: 'center', padding: 8 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: it.color || 'var(--text)' }}>{it.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{it.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressPct({ pct }) {
  const color = pct >= 75 ? 'var(--success)' : pct >= 40 ? 'var(--warning)' : 'var(--danger)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--gray-100, var(--surface-alt))', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color }}>{pct}%</span>
    </div>
  );
}
